// src/components/AdminContentPanel.tsx
import React, { useState } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  TextField,
  Dropdown,
  Modal,
  mergeStyles,
  Separator,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Icon,
} from '@fluentui/react';
import { Body1 } from '@fluentui/react-components';

import { H2, H3 } from '../Pages/Pages-styles';
import { Note } from '../graphql/types';

// === Типы ===

export interface VideoData {
  videoUrl: string;
  title?: string;
  description?: string;
}

export type ContentType = 'article' | 'video' | null;

export interface ActiveContent {
  type: ContentType;
  data: Note | VideoData | null;
}

export interface AdminContentPanelProps {
  activeContent: ActiveContent;
  loading: boolean;
  canEdit: boolean;
  availableNotes: Note[];
  onSaveArticle: (id: string) => Promise<void>;
  onDeleteArticle: () => Promise<void>;
  onSaveVideo: (url: string, title: string, description: string) => Promise<void>;
  onClearVideo: () => Promise<void>;
  apiURL: string;
  contentMode?: 'fixed' | 'dynamic';
}

// === Стили (остаются те же) ===
const iframeStyle = mergeStyles({
  width: '100%',
  height: '100%',
  border: 'none',
});

const containerStyle = mergeStyles({
  width: '100%',
  maxWidth: '854px',
  margin: '10px 2px',
  border: '1px solid #fff',
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  position: 'relative',
  aspectRatio: '16 / 9',
  overflow: 'hidden',
});

const articleContentStyle = mergeStyles({
  width: '100%',
  maxHeight: '100%',     // ← важно: ограничивает высоту по родителю
  overflowY: 'auto',     // ← теперь будет работать
  padding: '16px',
  boxSizing: 'border-box',
  lineHeight: '1.6',
  color: '#333',

  // Дополнительно: стилизация скролла (опционально)
  '::webkitScrollbar': { width: 6 },
  '::webkitScrollbarTrack': { background: '#f1f1f1' },
  '::webkitScrollbarThumb': { background: '#ccc', borderRadius: 3 },
});

// === Компонент ===
const AdminContentPanel: React.FC<AdminContentPanelProps> = ({
  activeContent,
  loading,
  canEdit,
  availableNotes,
  onSaveArticle,
  onDeleteArticle,
  onSaveVideo,
  onClearVideo,
  apiURL,
  contentMode,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contentType, setContentType] = useState<'article' | 'video'>('article');
  const [selectedNoteId, setSelectedNoteId] = useState<string>('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoDescription, setVideoDescription] = useState('');

  const getContainerStyles = (
    mode: 'fixed' | 'dynamic',
    contentType: ContentType
  ) =>
    mergeStyles({
      width: '100%',
      maxWidth: '854px',
      margin: '10px 2px',
      border: '1px solid #fff',
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      position: 'relative',
      overflow: 'hidden',

      // Только если режим fixed ИЛИ это видео — сохраняем aspect ratio
      ...(mode === 'fixed' || contentType === 'video'
        ? { aspectRatio: '16 / 9' }
        : { height: 'auto' }), // Для статьи в dynamic режиме — высота по контенту

      // Для статьи: если dynamic режим — не растягиваем на 100% высоты
      ...(contentType === 'article' && mode === 'dynamic'
        ? { minHeight: 'unset', height: 'auto' }
        : {}),
    });

  const openModal = () => {
    if (activeContent.type === 'article') {
      const note = activeContent.data as Note;
      console.log('📎 Открываем модалку: выбранная статья id=', note.id); // ← Лог
      setSelectedNoteId(note.id);
    }
    if (activeContent.type === 'article') {
      setSelectedNoteId((activeContent.data as Note).id);
      setContentType('article');
    } else if (activeContent.type === 'video') {
      const video = activeContent.data as VideoData;
      setVideoTitle(video.title || '');
      setVideoDescription(video.description || '');
      setVideoUrl(video.videoUrl || '');
      setContentType('video');
    } else {
      setContentType('article');
      setSelectedNoteId('');
      setVideoTitle('');
      setVideoDescription('');
      setVideoUrl('');
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (contentType === 'article') {
      if (!selectedNoteId) {
        alert('Выберите статью');
        return;
      }
      await onSaveArticle(selectedNoteId);
    } else {
      if (!videoUrl) {
        alert('Введите ссылку на видео');
        return;
      }
      await onSaveVideo(videoUrl, videoTitle, videoDescription);
    }
    setIsModalOpen(false);
  };

  const handleClear = async () => {
    if (activeContent.type === 'video') {
      await onClearVideo();
    } else if (activeContent.type === 'article') {
      await onDeleteArticle();
    }
  };

  if (loading && !activeContent.data) return <div>Загрузка...</div>;

  const renderContent = () => {
    if (activeContent.type === 'video') {
      const embedUrl = (activeContent.data as VideoData).videoUrl.replace(
        'rutube.ru/video/',
        'rutube.ru/play/embed/'
      );
      return (
        <iframe src={embedUrl} className={iframeStyle} title={(activeContent.data as VideoData).title} allowFullScreen />
      );
    }

    if (activeContent.type === 'article') {
      if (!activeContent.data) return null;
      const note = activeContent.data as Note;
      return (
        <div className={articleContentStyle}>
          <H2>{note.title}</H2>
          <div dangerouslySetInnerHTML={{ __html: note.content }} />

          {note.tags && note.tags.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
              {note.tags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    color: '#0078d4',
                    fontWeight: 500,
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {note.attachments && note.attachments.length > 0 && (
            <div style={{ marginTop: '32px' }}>
              <H3>Вложения</H3>
              <DetailsList
                items={note.attachments}
                columns={[
                  {
                    key: 'column-file',
                    name: 'Файл',
                    fieldName: 'filename',
                    minWidth: 200,
                    maxWidth: 300,
                    isRowHeader: true,
                    isResizable: true,
                    onRender: (item: any) => (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon iconName="Page" styles={{ root: { marginRight: 8 } }} />
                        <Body1>{item.filename}</Body1>
                      </div>
                    ),
                  },
                  {
                    key: 'column-size',
                    name: 'Размер',
                    fieldName: 'size',
                    minWidth: 100,
                    maxWidth: 120,
                    isResizable: true,
                    onRender: (item: any) => {
                      const sizeInBytes = item.size;
                      const size =
                        sizeInBytes < 1024 * 1024
                          ? (sizeInBytes / 1024).toFixed(1) + ' KB'
                          : (sizeInBytes / (1024 * 1024)).toFixed(1) + ' MB';
                      return <Body1>{size}</Body1>;
                    },
                  },
                  {
                    key: 'column-download',
                    name: 'Скачать',
                    fieldName: 'download',
                    minWidth: 120,
                    maxWidth: 140,
                    isResizable: false,
                    onRender: (item: any) => (
                      <a href={`${apiURL}/attachments/${item.id}`} download={item.filename} style={{ textDecoration: 'none' }}>
                        <DefaultButton text="Скачать" />
                      </a>
                    ),
                  },
                ]}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                compact={false}
                styles={{ root: { tableLayout: 'fixed', width: '100%' } }}
              />
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {activeContent.type && (
        <div className={getContainerStyles(contentMode || 'fixed', activeContent.type)}>
          {renderContent()}
        </div>
      )}
      {activeContent.type && (
        <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto', padding: '0 20px', boxSizing: 'border-box' }}>
          <Separator />
        </div>
      )}

      {canEdit && (
        <Stack horizontal tokens={{ childrenGap: 8 }} style={{ padding: '8px', justifyContent: 'center', marginTop: '16px' }}>
          <PrimaryButton text="Редактировать" onClick={openModal} />
          {activeContent.type && <DefaultButton text="Очистить" onClick={handleClear} />}
        </Stack>
      )}

      {canEdit && (
        <Modal isOpen={isModalOpen} onDismiss={() => setIsModalOpen(false)} isBlocking={false}>
          <Stack styles={{ root: { padding: '20px', width: '800px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' } }}>
            <Text variant="xLarge" block style={{ marginBottom: 16 }}>
              Редактирование контента
            </Text>

            <Dropdown
              label="Тип контента"
              options={[
                { key: 'article', text: 'Статья' },
                { key: 'video', text: 'Видео с Rutube' },
              ]}
              selectedKey={contentType}
              onChange={(_, opt) => setContentType(opt?.key as 'article' | 'video')}
            />

            {contentType === 'article' ? (
              <Dropdown
                label="Выберите статью"
                placeholder="Выберите из существующих"
                options={availableNotes.map((n) => ({ key: n.id, text: n.title }))}
                selectedKey={selectedNoteId}
                onChange={(_, opt) => setSelectedNoteId(opt?.key as string)}
              />
            ) : (
              <>
                <TextField
                  label="Заголовок видео"
                  value={videoTitle}
                  onChange={(_, value) => setVideoTitle(value || '')}
                  required
                />
                <TextField
                  label="Ссылка на видео"
                  placeholder="https://rutube.ru/video/..."
                  value={videoUrl}
                  onChange={(_, value) => setVideoUrl(value || '')}
                  required
                />
                <TextField
                  label="Описание"
                  multiline
                  value={videoDescription}
                  onChange={(_, value) => setVideoDescription(value || '')}
                />
              </>
            )}

            <Stack horizontal tokens={{ childrenGap: 8 }} style={{ marginTop: 20 }}>
              <PrimaryButton text="Сохранить" onClick={handleSave} />
              <DefaultButton text="Отмена" onClick={() => setIsModalOpen(false)} />
            </Stack>
          </Stack>
        </Modal>
      )}
    </div>
  );
};

export default AdminContentPanel;